/* eslint-disable no-nested-ternary */
import React, { Component } from "react";
import client, { signUpload } from "@culturehq/client";
import { DateTimeField, EmailField, Form, ImageField, ModalDialog, SelectField, StringField, SubmitButton } from "@culturehq/components";

import { useProfile, useUserSelectOptions } from "app/context";
import { useFieldsLabels } from "app/hooks";
import { canViewCompaniesField } from "app/permissions";
import { t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

export const roles = [
  { label: t("Super Admin - This account will have full access to view/edit content, users, settings, and analytics."), value: "super_admin" },
  { label: t("Content Admin - This account will have user access and limited admin access to manage and review content."), value: "content_admin" },
  { label: t("Cultural Ambassador - This account will be able to share their own stories on social networks without the need for administrator approval."), value: "cultural_ambassador" },
  { label: t("Recruiter - This account will be able to map stories to job listings on your website."), value: "recruiter" },
  { label: t("User - This account will only be able to contribute stories and engage with content."), value: "regular" },
  { label: t("Contributor - This account will not go through the onboarding flow to fill out their profile, but will have regular user access."), value: "collaborator" }
];

const visibilityOptions = [
  { label: t("Enabled"), value: "approved" },
  { label: t("Disabled"), value: "unapproved" },
  { label: t("Unapproved"), value: "pending" }
];

const getInitialValues = ({
  active,
  avatar,
  location,
  companies,
  departments,
  classYear,
  programs,
  degree,
  visibility,
  ...initialValues
}) => {
  const getVisibilityValue = () => {
    if (visibility === "unapproved" || visibility === "pending") return "pending";

    if (!active) return "unapproved";

    return "approved";
  };

  return {
    avatar: avatar.originalUrl,
    locationName: location?.name,
    companyNames: companies?.map(({ name }) => name),
    departmentNames: departments.map(({ name }) => name),
    classYearName: classYear?.name,
    degreeName: degree?.name,
    programNames: programs.map(({ name }) => name),
    ...initialValues,
    visibility: getVisibilityValue()
  };
};

class UserEditModalContext extends Component {
  state = { error: false, imageProgress: null };

  handleSubmit = async ({ avatar, ...others }) => {
    const { onChange, user: { id: userId } } = this.props;

    this.setState({ error: false });

    try {
      // we make this to just have one single status for the clients,
      // but internally there is a difference between visibility and the active status
      const values = {
        ...others,
        visibility: others.visibility === "pending" ? "unapproved" : others.visibility,
        active: others.visibility !== "unapproved"
      };
      if (avatar && avatar.size) {
        values.avatarUrl = await signUpload(avatar, imageProgress => {
          this.setState({ imageProgress });
        });
      }
      const { user } = await client.updateUser({ ...values, userId });

      onChange(user);
    } catch (error) {
      if (error.status === 422) {
        this.setState({ error });
      } else {
        throw error;
      }
    }
  };

  render() {
    const {
      classYearOptions,
      companyOptions,
      departmentOptions,
      labels,
      locationOptions,
      programOptions,
      profile: { organization },
      degreeOptions,
      onClose,
      user
    } = this.props;
    const { error, imageProgress } = this.state;
    const offset = -new Date().getTimezoneOffset();

    return (
      <ChqModalDialog className="ManageModal ManageUserModal" onClose={onClose}>
        <ModalDialog.Heading onClose={onClose}>
          <Trans>Edit User</Trans>
        </ModalDialog.Heading>
        <ModalDialog.Body>
          {error && <ErrorMessage error={error} />}
          <Form onSubmit={this.handleSubmit} initialValues={getInitialValues(user)}>
            <ImageField name="avatar" aspectRatio={1} progress={imageProgress}>
              <Trans>Profile Photo</Trans>
            </ImageField>
            <EmailField name="email" required>
              <Trans>E-mail Address *</Trans>
            </EmailField>
            {organization.firstNameLastNameEnabled ? (
              <>
                <StringField name="firstName" required>
                  <Trans>Invitee First Name *</Trans>
                </StringField>
                <StringField name="lastName" required>
                  <Trans>Invitee Last Name *</Trans>
                </StringField>
              </>
            ) : (
              <StringField name="name" required>
                <Trans>Invitee Name *</Trans>
              </StringField>
            )}
            {!organization.isEducational && (
              <StringField name="title">
                <Trans>Title</Trans>
              </StringField>
            )}
            <SelectField name="locationName" creatable options={locationOptions} required>
              <Trans>Location *</Trans>
            </SelectField>
            {!organization.isEducational && (
              <SelectField name="departmentNames" creatable multiple options={departmentOptions}>
                {labels.departments.multipleField}
              </SelectField>
            )}
            {canViewCompaniesField(organization) && (
              <SelectField name="companyNames" creatable multiple options={companyOptions}>
                <Trans>{labels.companies.multipleField}</Trans>
              </SelectField>
            )}
            {!organization.isEducational && (
              <DateTimeField name="startedOn" offset={offset}>
                <Trans>Start Date At The Company</Trans>
              </DateTimeField>
            )}
            {organization.isEducational && (
              <>
                <SelectField name="degreeName" creatable options={degreeOptions}>
                  <Trans>Degree</Trans>
                </SelectField>
                <SelectField name="programNames" creatable multiple options={programOptions}>
                  <Trans>Programs</Trans>
                </SelectField>
                <SelectField name="classYearName" creatable options={classYearOptions}>
                  <Trans>{organization.name === "Dartmouth Guarini" ? "Years in Program" : "Class year"}</Trans>
                </SelectField>
              </>
            )}
            <SelectField
              name="role"
              options={roles}
              required
            >
              <Trans>Account Role</Trans>
            </SelectField>
            <SelectField
              name="visibility"
              options={visibilityOptions}
            >
              <Trans>Access Level</Trans>
            </SelectField>
            <SubmitButton primary>
              {submitting => (submitting ? t("Submitting...") : t("Save"))}
            </SubmitButton>
          </Form>
        </ModalDialog.Body>
      </ChqModalDialog>
    );
  }
}

const UserEditModal = props => {
  const { profile } = useProfile();
  const {
    classYearOptions,
    companyOptions,
    departmentOptions,
    locationOptions,
    programOptions,
    degreeOptions
  } = useUserSelectOptions();
  const labels = useFieldsLabels();

  return (
    <UserEditModalContext
      {...props}
      classYearOptions={classYearOptions}
      companyOptions={companyOptions}
      departmentOptions={departmentOptions}
      labels={labels}
      locationOptions={locationOptions}
      programOptions={programOptions}
      profile={profile}
      degreeOptions={degreeOptions}
    />
  );
};

export default UserEditModal;
